// client/src/pages/ChangePlan.js
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useSettings } from '../hooks/useSettings.js';
import { useAuth } from '../hooks/useAuth.js';
import Button from '../components/ui/Button.js';
import api from '../services/api.js';

export default function ChangePlan() {
  const { userTypes } = useSettings();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedInterval, setSelectedInterval] = useState(
    searchParams.get('interval') || 'monthly'
  );
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const paidTiers = (userTypes || []).filter((t) => t.ispaid && t.isenabled);
  const currentTier = (userTypes || []).find((t) => t.usertypeid === user?.usertype);

  // Trial detection
  const isOnTrial = user?.trial_expires_at && new Date(user.trial_expires_at) > new Date() && !user.stripecustomerid;

  // Pre-select tier from URL params
  const preselectedTierId = parseInt(searchParams.get('tier'));

  useEffect(() => {
    if (preselectedTierId && paidTiers.length > 0 && !selectedTier) {
      const found = paidTiers.find((t) => t.usertypeid === preselectedTierId);
      if (found && found.usertypeid !== user?.usertype) {
        setSelectedTier(found);
        sessionStorage.removeItem('pendingPlan');
      }
    }
  }, [preselectedTierId, paidTiers, selectedTier, user?.usertype]);

  // Fetch proration preview when tier/interval changes
  useEffect(() => {
    if (!selectedTier) {
      setPreview(null);
      return;
    }

    const fetchPreview = async () => {
      setLoadingPreview(true);
      setError(null);
      setPreview(null);

      try {
        const data = await api.get(
          `/subscriptions/preview-change?tier=${selectedTier.usertypeid}&interval=${selectedInterval}`
        );
        setPreview(data);
      } catch (err) {
        // The subscription drifted out of an active state (canceled, unpaid,
        // missing in Stripe). The server has already reconciled our records;
        // a canceled sub can't be "changed", so route the user to pricing to
        // start a fresh subscription instead of showing a dead-end error.
        if (err.data?.error === 'no_active_subscription') {
          navigate('/pricing');
          return;
        }
        setError(err.data?.error || 'Unable to preview plan change');
      } finally {
        setLoadingPreview(false);
      }
    };

    fetchPreview();
  }, [selectedTier, selectedInterval]);

  const handleConfirm = async () => {
    if (!selectedTier) return;

    setConfirming(true);
    setError(null);

    try {
      const data = await api.post('/subscriptions/change-plan', {
        tier: selectedTier.usertypeid,
        interval: selectedInterval,
      });

      setSuccess(data.newTier);

      setTimeout(() => {
        navigate('/account');
        window.location.reload();
      }, 2000);
    } catch (err) {
      // Same drift case as the preview: the sub is no longer active, so
      // there's nothing to change. Send them to pricing to re-subscribe.
      if (err.data?.error === 'no_active_subscription') {
        navigate('/pricing');
        return;
      }
      setError(err.data?.error || 'Unable to change plan');
    } finally {
      setConfirming(false);
    }
  };

  const formatCents = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  // ─── Theming note ────────────────────────────────────────
  // Cards use the same treatment as Account.js: a deep navy surface in BOTH
  // light and dark modes (solid royal-900 in light, translucent royal-800 in
  // dark) with light text inside. This keeps Change Plan visually consistent
  // with the rest of the account area on the cream light-mode background.
  const cardClass = 'bg-royal-900 dark:bg-royal-800/40 border border-royal-700 dark:border-royal-600';

  // Trial user — redirect to pricing (they have no Stripe subscription to change)
  if (user && isOnTrial) {
    return (
      <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <h1 className="font-display text-3xl text-gold-dark dark:text-gold mb-4">Change Plan</h1>
          <p className="text-royal-600 dark:text-royal-200 mb-2">You're currently on a free trial of <strong className="text-royal-900 dark:text-white">{currentTier?.typename}</strong>.</p>
          <p className="text-royal-600 dark:text-royal-200 mb-6">Subscribe to a plan from the pricing page to get started.</p>
          <Link to="/pricing" className="text-gold-dark dark:text-gold hover:text-gold dark:hover:text-gold-light transition-colors font-medium">
            View Plans &rarr;
          </Link>
        </div>
      </div>
    );
  }

  // Not on a paid plan
  if (user && !user.stripecustomerid) {
    return (
      <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <h1 className="font-display text-3xl text-gold-dark dark:text-gold mb-4">Change Plan</h1>
          <p className="text-royal-600 dark:text-royal-200 mb-6">You're currently on the Free plan. Subscribe to a paid plan first.</p>
          <Link to="/pricing" className="text-gold-dark dark:text-gold hover:text-gold dark:hover:text-gold-light transition-colors">
            View Plans &rarr;
          </Link>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
            <svg className="w-8 h-8 text-green-500 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="font-display text-2xl text-gold-dark dark:text-gold mb-2">Plan Changed!</h2>
          <p className="text-royal-600 dark:text-royal-200">You're now on the {success} plan. Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
      <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-display text-3xl text-gold-dark dark:text-gold mb-2">Change Your Plan</h1>
          {currentTier && (
            <p className="text-royal-600 dark:text-royal-200">
              Currently on <span className="text-royal-900 dark:text-white font-medium">{currentTier.typename}</span>
            </p>
          )}
        </div>

        {/* Interval toggle — navy pill in both themes */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex rounded-full border border-royal-700 dark:border-royal-600 p-1 bg-royal-900 dark:bg-royal-800">
            <button
              onClick={() => setSelectedInterval('monthly')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                selectedInterval === 'monthly'
                  ? 'bg-gold text-royal-900'
                  : 'text-white hover:text-gold'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setSelectedInterval('yearly')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                selectedInterval === 'yearly'
                  ? 'bg-gold text-royal-900'
                  : 'text-white hover:text-gold'
              }`}
            >
              Yearly
            </button>
          </div>
        </div>

        {/* Tier cards — adaptive centered grid. Navy surface in both themes
            (matches Account.js), with gold accents for the selected card. */}
        <div className={`grid gap-4 sm:gap-5 mb-8 mx-auto ${
          paidTiers.length <= 2 ? 'grid-cols-1 sm:grid-cols-2 max-w-2xl' : 'grid-cols-1 md:grid-cols-3 max-w-5xl'
        }`}>
          {paidTiers.map((tier) => {
            const isCurrent = tier.usertypeid === user?.usertype;
            const isSelected = selectedTier?.usertypeid === tier.usertypeid;
            const monthly = parseFloat(tier.pricemonthly) || 0;
            const yearly = parseFloat(tier.priceyearly) || 0;
            const price = selectedInterval === 'yearly' ? yearly : monthly;

            return (
              <button
                key={tier.usertypeid}
                onClick={() => !isCurrent && setSelectedTier(tier)}
                disabled={isCurrent}
                className={`text-left rounded-2xl p-5 sm:p-6 border transition-all bg-royal-900 dark:bg-royal-800/40 ${
                  isCurrent
                    ? 'border-royal-700 dark:border-royal-600 opacity-60 cursor-not-allowed'
                    : isSelected
                      ? 'border-gold ring-1 ring-gold shadow-lg shadow-gold/10'
                      : 'border-royal-700 dark:border-royal-600 hover:border-gold/60 cursor-pointer'
                }`}
              >
                {isCurrent && (
                  <span className="text-sm font-bold text-royal-100 bg-royal-700 px-2 py-0.5 rounded-full">
                    Current Plan
                  </span>
                )}
                {tier.ispopular && !isCurrent && (
                  <span className="text-sm font-bold text-royal-900 bg-gold-gradient px-2 py-0.5 rounded-full">
                    Popular
                  </span>
                )}

                <h3 className="font-display text-lg text-gold mt-2">{tier.typename}</h3>
                <p className="text-royal-200 text-sm mb-3">{tier.tagline}</p>

                <div className="mb-3">
                  <span className="text-2xl font-bold text-white">${price.toFixed(2)}</span>
                  <span className="text-royal-200 text-sm">/{selectedInterval === 'yearly' ? 'year' : 'month'}</span>
                </div>

                <ul className="space-y-1">
                  {(tier.features || []).slice(0, 4).map((f, i) => (
                    <li key={i} className="text-sm text-royal-50 flex items-start gap-1.5">
                      <span className="text-gold mt-0.5 shrink-0">&#10003;</span>
                      <span>{f}</span>
                    </li>
                  ))}
                  {(tier.features || []).length > 4 && (
                    <li className="text-sm text-royal-200">+{tier.features.length - 4} more</li>
                  )}
                </ul>
              </button>
            );
          })}
        </div>

        {/* Proration preview */}
        {selectedTier && (
          <div className="max-w-lg mx-auto">
            <div className={`${cardClass} rounded-2xl p-6 mb-6 shadow-sm`}>
              <h3 className="text-white font-medium mb-4">Plan Change Summary</h3>

              {loadingPreview ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin w-6 h-6 border-2 border-gold border-t-transparent rounded-full" />
                  <span className="ml-3 text-royal-200 text-sm">Calculating...</span>
                </div>
              ) : preview ? (
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-royal-200">Switching to</span>
                    <span className="text-white font-medium">
                      {selectedTier.typename} ({selectedInterval})
                    </span>
                  </div>

                  {preview.credit > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-royal-200">Credit from current plan</span>
                      <span className="text-green-400">-{formatCents(preview.credit)}</span>
                    </div>
                  )}

                  <div className="border-t border-royal-700 pt-3 flex justify-between">
                    <span className="text-royal-100 font-medium">Due now</span>
                    <span className="text-white font-bold text-lg">
                      {formatCents(preview.amountDue)}
                    </span>
                  </div>

                  {preview.amountDue === 0 && preview.credit > 0 && (
                    <p className="text-sm text-royal-200">
                      Your remaining credit covers the switch. No charge today.
                    </p>
                  )}

                  <p className="text-sm text-royal-200 mt-2">
                    After this, you'll be billed ${preview.newPrice.toFixed(2)}/{preview.newInterval === 'yearly' ? 'year' : 'month'} going forward.
                  </p>
                </div>
              ) : error ? (
                <p className="text-red-400 text-sm">{error}</p>
              ) : null}
            </div>

            {/* Error — sits on the page background (cream in light), so it keeps
                the page-level theme-aware red treatment rather than the navy card one. */}
            {error && !loadingPreview && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-lg">
                <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
              </div>
            )}

            {/* Confirm button */}
            {preview && !error && (
              <Button
                onClick={handleConfirm}
                disabled={confirming || loadingPreview}
                className="w-full text-base py-3"
              >
                {confirming ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Switching plan...
                  </span>
                ) : preview.amountDue > 0 ? (
                  `Confirm — Pay ${formatCents(preview.amountDue)} now`
                ) : (
                  'Confirm Plan Change'
                )}
              </Button>
            )}
          </div>
        )}

        {/* No selection prompt */}
        {!selectedTier && (
          <p className="text-center text-royal-500 dark:text-royal-200 text-sm">Select a plan above to see your proration details.</p>
        )}

        {/* Back link */}
        <div className="text-center mt-8">
          <Link to="/account" className="text-royal-500 dark:text-royal-200 hover:text-gold-dark dark:hover:text-gold transition-colors text-sm">
            &larr; Back to Account
          </Link>
        </div>
      </div>
    </div>
  );
}